import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Lock, Mail, Eye, EyeOff, Sparkles, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

export const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetting, setResetting] = useState(false);
  
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleReset = async () => {
    setResetting(true);
    setError('');
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/reset-admin-user`, {
        method: 'POST'
      });
      const data = await res.json();
      
      if (res.ok) {
        alert('✅ Admin účet resetován!\n\nEmail: admin@kuja.cz\nHeslo: Kuja2026!');
        setEmail('admin@kuja.cz');
        setPassword('Kuja2026!');
      } else {
        setError('Reset se nezdařil');
      }
    } catch (err) {
      setError('Chyba při resetu');
    } finally {
      setResetting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/admin');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 relative overflow-hidden flex items-center justify-center">
      {/* Animated background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 -left-4 w-96 h-96 bg-cyan-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob"></div>
        <div className="absolute top-0 -right-4 w-96 h-96 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-2000"></div>
        <div className="absolute -bottom-8 left-20 w-96 h-96 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-4000"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-md mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-3 mb-6 px-6 py-3 rounded-full bg-gradient-to-r from-cyan-500/20 to-blue-500/20 backdrop-blur-xl border border-cyan-400/30">
              <Sparkles className="text-cyan-400 animate-pulse" size={24} />
              <span className="text-cyan-400 font-bold text-sm tracking-wider uppercase">
                Admin Přístup
              </span>
              <Sparkles className="text-blue-400 animate-pulse" size={24} />
            </div>
            
            <h1 className="text-4xl sm:text-5xl font-black mb-4 bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 bg-clip-text text-transparent">
              Přihlášení
            </h1>
            <p className="text-slate-300 text-lg">
              Čištění vozidel Kuja
            </p>
          </div>

          {/* Login Card */}
          <Card className="bg-slate-800/90 backdrop-blur-2xl border-2 border-cyan-500/30 shadow-2xl shadow-cyan-500/20">
            <CardHeader className="bg-gradient-to-r from-cyan-500/20 via-blue-500/20 to-purple-500/20 border-b-2 border-cyan-500/30">
              <CardTitle className="text-2xl font-black text-cyan-400 flex items-center gap-3 justify-center">
                <Lock size={28} />
                Administrace
              </CardTitle>
            </CardHeader>
            
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Email */}
                <div className="space-y-3">
                  <Label className="text-cyan-400 font-bold flex items-center gap-2">
                    <Mail size={18} />
                    Email
                  </Label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="admin@example.com"
                    className="h-12 bg-slate-900/50 border-2 border-slate-700 focus:border-cyan-500 text-white placeholder:text-slate-500 rounded-xl"
                    required
                    disabled={loading}
                  />
                </div>

                {/* Password */}
                <div className="space-y-3">
                  <Label className="text-cyan-400 font-bold flex items-center gap-2">
                    <Lock size={18} />
                    Heslo
                  </Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="h-12 bg-slate-900/50 border-2 border-slate-700 focus:border-cyan-500 text-white placeholder:text-slate-500 rounded-xl pr-12"
                      required
                      disabled={loading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-cyan-400 transition-colors"
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>

                {/* Error Message */}
                {error && (
                  <div className="p-4 rounded-xl bg-red-500/10 border-2 border-red-500/30 flex items-start gap-3">
                    <AlertCircle className="text-red-400 flex-shrink-0 mt-0.5" size={20} />
                    <p className="text-red-400 text-sm font-medium">{error}</p>
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full h-14 bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-500 hover:from-cyan-600 hover:via-blue-600 hover:to-purple-600 text-white font-black rounded-xl transition-all duration-300 flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-cyan-500/30"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                      Přihlašování...
                    </>
                  ) : (
                    <>
                      <Lock size={20} />
                      Přihlásit se
                    </>
                  )}
                </button>

                {/* Reset Admin Button */}
                <button
                  type="button"
                  onClick={handleReset}
                  disabled={resetting || loading}
                  className="w-full mt-3 py-3 bg-slate-700/50 hover:bg-slate-600/50 text-slate-300 hover:text-white font-bold rounded-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  {resetting ? 'Resetování...' : '🔧 Nefunguje přihlášení? Klikněte pro reset'}
                </button>
              </form>
            </CardContent>
          </Card>

          {/* Footer Note */}
          <p className="text-center mt-6 text-slate-400 text-sm">
            🔒 Zabezpečené připojení
          </p>
        </div>
      </div>

      <style>{`
        @keyframes blob {
          0%, 100% { transform: translate(0, 0) scale(1); }
          25% { transform: translate(20px, -50px) scale(1.1); }
          50% { transform: translate(-20px, 20px) scale(0.9); }
          75% { transform: translate(50px, 50px) scale(1.05); }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </div>
  );
};
